import { supabase } from '../supabase';

export interface WritingCategory {
  id: number;
  name_ml: { it: string; bn: string; en: string };
  description_ml: { it: string; bn: string; en: string };
  // DB uses `icon_name`, but we also support legacy `icon` from fallback data
  icon_name?: string | null;
  icon?: string | null;
  color: string;
  order_index: number;
}

export interface WritingExercise {
  id: number;
  category_id: number;
  title_ml: { it: string; bn: string; en: string };
  prompt_ml: { it: string; bn: string; en: string };
  example_answer_ml?: { it: string; bn: string; en: string } | null;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  order_index: number;
}

// ─── Fallback data ────────────────────────────────────────────────────────────

export const FALLBACK_CATEGORIES: WritingCategory[] = [
  {
    id: 1,
    name_ml: { it: 'Saluti', bn: 'অভিবাদন', en: 'Greetings' },
    description_ml: { it: 'Impara a scrivere saluti', bn: 'অভিবাদন লিখতে শিখুন', en: 'Learn to write greetings' },
    icon: '👋',
    color: '#7C3AED',
    order_index: 1,
  },
  {
    id: 2,
    name_ml: { it: 'Presentazioni', bn: 'পরিচয়', en: 'Introductions' },
    description_ml: { it: 'Come presentarti in italiano', bn: 'ইতালীয় ভাষায় নিজের পরিচয় দিন', en: 'How to introduce yourself' },
    icon: '🙋',
    color: '#10B981',
    order_index: 2,
  },
  {
    id: 3,
    name_ml: { it: 'Shopping', bn: 'কেনাকাটা', en: 'Shopping' },
    description_ml: { it: 'Frasi per fare shopping', bn: 'কেনাকাটার বাক্য লিখুন', en: 'Write shopping phrases' },
    icon: '🛍️',
    color: '#F59E0B',
    order_index: 3,
  },
  {
    id: 4,
    name_ml: { it: 'Email e Messaggi', bn: 'ইমেইল ও বার্তা', en: 'Emails & Messages' },
    description_ml: { it: 'Scrivi email e messaggi formali', bn: 'আনুষ্ঠানিক ইমেইল ও বার্তা লিখুন', en: 'Write formal emails and messages' },
    icon: '✉️',
    color: '#EF4444',
    order_index: 4,
  },
  {
    id: 5,
    name_ml: { it: 'Racconti Brevi', bn: 'ছোট গল্প', en: 'Short Stories' },
    description_ml: { it: 'Scrivi brevi racconti in italiano', bn: 'ইতালীয় ভাষায় ছোট গল্প লিখুন', en: 'Write short stories in Italian' },
    icon: '📖',
    color: '#3B82F6',
    order_index: 5,
  },
  {
    id: 6,
    name_ml: { it: 'Lavoro', bn: 'কাজ', en: 'Work' },
    description_ml: { it: 'Frasi utili per il lavoro', bn: 'কাজের জন্য দরকারী বাক্য', en: 'Useful phrases for work' },
    icon: '💼',
    color: '#8B5CF6',
    order_index: 6,
  },
];

const FALLBACK_EXERCISES: Record<number, WritingExercise[]> = {
  1: [
    {
      id: 101, category_id: 1, difficulty: 'Beginner', order_index: 1,
      title_ml: { it: 'Scrivi un saluto', bn: 'একটি অভিবাদন লিখুন', en: 'Write a greeting' },
      prompt_ml: { it: 'Scrivi come salutare qualcuno informalmente', bn: 'অনানুষ্ঠানিকভাবে কাউকে কীভাবে অভিবাদন জানাবেন তা লিখুন', en: 'Write how to greet someone informally' },
      example_answer_ml: { it: 'Ciao! Come stai? Spero che tu stia bene.', bn: 'হ্যালো! তুমি কেমন আছো? আশা করি তুমি ভালো আছো।', en: 'Hello! How are you? I hope you are well.' },
    },
    {
      id: 102, category_id: 1, difficulty: 'Beginner', order_index: 2,
      title_ml: { it: 'Saluto formale', bn: 'আনুষ্ঠানিক অভিবাদন', en: 'Formal greeting' },
      prompt_ml: { it: 'Scrivi un saluto formale per un incontro di lavoro', bn: 'একটি কাজের মিটিংয়ের জন্য আনুষ্ঠানিক অভিবাদন লিখুন', en: 'Write a formal greeting for a work meeting' },
      example_answer_ml: { it: 'Buongiorno, piacere di conoscerla. Mi chiamo Marco.', bn: 'শুভ সকাল, আপনার সাথে পরিচিত হতে পেরে খুশি। আমার নাম মার্কো।', en: 'Good morning, pleased to meet you. My name is Marco.' },
    },
  ],
  2: [
    {
      id: 201, category_id: 2, difficulty: 'Beginner', order_index: 1,
      title_ml: { it: 'Presentati', bn: 'নিজেকে পরিচয় দিন', en: 'Introduce yourself' },
      prompt_ml: { it: 'Scrivi una breve presentazione di te stesso in italiano', bn: 'ইতালীয় ভাষায় নিজের সংক্ষিপ্ত পরিচয় লিখুন', en: 'Write a brief introduction about yourself in Italian' },
      example_answer_ml: { it: 'Mi chiamo Luca, ho 25 anni e sono di Milano. Lavoro come insegnante e mi piace viaggiare.', bn: 'আমার নাম লুকা, আমার বয়স ২৫ বছর এবং আমি মিলান থেকে। আমি শিক্ষক হিসেবে কাজ করি এবং ভ্রমণ পছন্দ করি।', en: 'My name is Luca, I am 25 years old and I am from Milan. I work as a teacher and I like to travel.' },
    },
  ],
  3: [
    {
      id: 301, category_id: 3, difficulty: 'Intermediate', order_index: 1,
      title_ml: { it: 'Al negozio', bn: 'দোকানে', en: 'At the shop' },
      prompt_ml: { it: 'Scrivi come chiedere il prezzo di un prodotto in un negozio', bn: 'দোকানে একটি পণ্যের দাম কীভাবে জিজ্ঞেস করবেন তা লিখুন', en: 'Write how to ask the price of a product in a shop' },
      example_answer_ml: { it: 'Scusi, quanto costa questa camicia? È in saldo? Posso provarlo?', bn: 'দয়া করে, এই শার্টের দাম কত? এটা কি সেলে আছে? আমি কি এটা পরে দেখতে পারি?', en: 'Excuse me, how much does this shirt cost? Is it on sale? Can I try it on?' },
    },
  ],
  4: [
    {
      id: 401, category_id: 4, difficulty: 'Intermediate', order_index: 1,
      title_ml: { it: 'Email formale', bn: 'আনুষ্ঠানিক ইমেইল', en: 'Formal email' },
      prompt_ml: { it: "Scrivi l'inizio di un'email formale al tuo capo", bn: 'আপনার বসকে একটি আনুষ্ঠানিক ইমেইলের শুরু লিখুন', en: 'Write the beginning of a formal email to your boss' },
      example_answer_ml: { it: 'Gentile Direttore Rossi, La contatto per informarLa riguardo alla riunione di domani.', bn: 'প্রিয় পরিচালক রোসি, আগামীকালের মিটিং সম্পর্কে আপনাকে জানাতে যোগাযোগ করছি।', en: 'Dear Director Rossi, I am contacting you to inform you about tomorrow\'s meeting.' },
    },
  ],
  5: [
    {
      id: 501, category_id: 5, difficulty: 'Advanced', order_index: 1,
      title_ml: { it: 'La mia giornata', bn: 'আমার দিন', en: 'My day' },
      prompt_ml: { it: 'Scrivi un breve racconto su come trascorri una tipica giornata', bn: 'আপনার একটি সাধারণ দিন কীভাবে কাটে তার একটি সংক্ষিপ্ত গল্প লিখুন', en: 'Write a short story about how you spend a typical day' },
      example_answer_ml: { it: 'Ogni mattina mi sveglio alle sette. Faccio colazione con un caffè e un cornetto, poi vado al lavoro in bicicletta.', bn: 'প্রতি সকালে আমি সাতটায় উঠি। আমি কফি এবং একটি ক্রোয়াসাঁ দিয়ে নাস্তা করি, তারপর সাইকেলে কাজে যাই।', en: 'Every morning I wake up at seven. I have breakfast with a coffee and a croissant, then I go to work by bicycle.' },
    },
  ],
  6: [
    {
      id: 601, category_id: 6, difficulty: 'Intermediate', order_index: 1,
      title_ml: { it: 'Al colloquio', bn: 'ইন্টারভিউতে', en: 'At the interview' },
      prompt_ml: { it: "Scrivi come risponderesti alla domanda 'Parlami di te' in un colloquio", bn: 'একটি ইন্টারভিউতে "আপনার সম্পর্কে বলুন" প্রশ্নের উত্তর কীভাবে দেবেন তা লিখুন', en: 'Write how you would answer "Tell me about yourself" in an interview' },
      example_answer_ml: { it: 'Ho una laurea in informatica e cinque anni di esperienza nel settore. Sono una persona creativa e mi piace lavorare in team.', bn: 'আমার কম্পিউটার বিজ্ঞানে স্নাতক ডিগ্রি আছে এবং এই ক্ষেত্রে পাঁচ বছরের অভিজ্ঞতা আছে। আমি একজন সৃজনশীল মানুষ এবং দলে কাজ করতে পছন্দ করি।', en: 'I have a degree in computer science and five years of experience in the field. I am a creative person and I like working in a team.' },
    },
  ],
};

// ─── API functions ────────────────────────────────────────────────────────────

export async function fetchWritingCategories(): Promise<WritingCategory[]> {
  try {
    const { data, error } = await supabase
      .from('writing_categories')
      .select('id, name_ml, description_ml, icon_name, color, order_index, is_published')
      .eq('is_published', true)
      .order('order_index', { ascending: true });
    if (error) throw error;
    return (data || []).map((c: any) => ({
      ...c,
      icon: c.icon_name ?? c.icon ?? null,
    }));
  } catch {
    return FALLBACK_CATEGORIES;
  }
}

export async function fetchWritingExercises(categoryId: number): Promise<WritingExercise[]> {
  try {
    const { data, error } = await supabase
      .from('writing_exercises')
      .select('*')
      .eq('category_id', categoryId)
      .eq('is_published', true)
      .order('order_index', { ascending: true });
    if (error) throw error;
    return data || [];
  } catch {
    return FALLBACK_EXERCISES[categoryId] || [];
  }
}

export async function saveWritingResponse(
  userId: string,
  exerciseId: number,
  response: string
): Promise<void> {
  try {
    await supabase.from('writing_responses').insert({
      user_id: userId,
      exercise_id: exerciseId,
      response,
      created_at: new Date().toISOString(),
    });
  } catch {
    // Non-critical — don't surface errors to user
  }
}
